import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { roles } from "../constants/commonConstants";
import showToast from "../utils/toastNotifications";
import { useEffect, useState } from "react";
import {
  checkAndSwitchRoleByPath,
  closeSignInModal,
  openSignInModal,
  switchRole,
} from "../store";
import ClientHome from "../pages/Client/ClientHome";
import TaskerHome from "../pages/Tasker/TaskerHome";
import Loader from "./Common/Loader";
import AuthenticationSkeleton from "./skeleton/AuthenticationSkeleton";

// NOTE: Component to protect client routes
export const ProtectedClientRoute = () => {
  const { isAuthenticated, role } = useSelector((state) => state.auth);
  // change role first
  const [roleUpdated, setRoleUpdated] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    // Update the role first
    if (role !== roles.USER) {
      dispatch(switchRole(roles.USER));
      setRoleUpdated(true);
    } else {
      setRoleUpdated(true);
    }
  }, [dispatch, role]);

  // If not authenticated, show sign-in modal
  if (!isAuthenticated) {
    // Open the sign-in modal
    dispatch(openSignInModal());

    // Render a blank outlet that will show the current route's UI
    // in the background while the modal is displayed on top
    return <AuthenticationSkeleton />;
  }

  // Show loader while role is updating
  if (!roleUpdated) {
    return <Loader />;
  }

  return <Outlet />;
};

// NOTE: Component to protect tasker routes
export const ProtectedTaskerRoute = () => {
  const { isAuthenticated, role, availableRoles, user } = useSelector(
    (state) => state.auth
  );
  const [roleUpdated, setRoleUpdated] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (availableRoles && availableRoles.includes(roles.TASKER)) {
      if (role !== roles.TASKER) {
        dispatch(switchRole(roles.TASKER));
        setRoleUpdated(true);
      } else {
        setRoleUpdated(true);
      }
    } else {
      setRoleUpdated(true);
    }
  }, [dispatch, role, availableRoles]);

  // If not authenticated, show sign-in modal
  if (!isAuthenticated) {
    // Open the sign-in modal
    dispatch(openSignInModal());

    // Render a blank outlet to keep the current route
    return <AuthenticationSkeleton />;
  }

  if (!roleUpdated) {
    return <Loader />;
  }

  // Check if user has requested to be a tasker but isn't verified yet
  if (user?.taskerRequestedAndStillNotVerified === true) {
    showToast(
      "info",
      "Your Service Provider account is under review. Please wait."
    );
    return <Navigate to="/service-pro" />;
  }
  // Check if user is not a tasker and hasn't requested to be one
  else if (!availableRoles || !availableRoles.includes(roles.TASKER)) {
    showToast("info", "Please register as a service provider");
    return <Navigate to="/tasker-signup" replace />;
  }
  // check if user is a tasker, but account is suspended
  // else if (user && user?.isSuspended) {
  //   showToast(
  //     "error",
  //     "Your service provider account has been suspended. Please contact support for more information."
  //   );
  //   return <Navigate to="/" />;
  // }
  // User is a verified tasker
  else {
    return <Outlet />;
  }
};

// NOTE: Component for protected common routes
export const ProtectedRoute = () => {
  const { isAuthenticated } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const location = useLocation();

  // Check and update role based on the current pathname
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(checkAndSwitchRoleByPath());
    }
  }, [dispatch, location.pathname, isAuthenticated]);

  // If not authenticated, show sign-in modal
  if (!isAuthenticated) {
    // Open the sign-in modal
    dispatch(openSignInModal());

    // Render a blank outlet to keep the current route
    return <AuthenticationSkeleton />;
  }

  return <Outlet />;
};

// NOTE: Updated ProtectedRedirect to handle landing page routing
export const ProtectedRedirect = () => {
  const { role } = useSelector((state) => state.auth);
  return role === roles.TASKER ? (
    <Navigate to="/service-pro" replace />
  ) : (
    <Navigate to="/client" replace />
  );
};

// NOTE: Handle Public Routes
export const PublicRoute = () => {
  const { isSignInModalOpen } = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  // Check wether signin model is open, if it is open then close it, beacuse we are in public route
  useEffect(() => {
    if (isSignInModalOpen) {
      dispatch(closeSignInModal());
    }
  }, [isSignInModalOpen]);

  return <Outlet />;
};

export const ClientHomePage = () => {
  const dispatch = useDispatch();
  const { role } = useSelector((state) => state.auth);

  useEffect(() => {
    if (role !== roles.USER) {
      dispatch(switchRole(roles.USER));
    }
  }, [dispatch, role]);

  return <ClientHome />;
};

export const TaskerHomePage = () => {
  const dispatch = useDispatch();
  const { role } = useSelector((state) => state.auth);

  useEffect(() => {
    if (role !== roles.TASKER) {
      dispatch(switchRole(roles.TASKER));
    }
  }, [dispatch, role]);

  return <TaskerHome />;
};
